import { excludeWorkoutNotes } from "./section-types";

const base = 64;

/**
 * Given an exercise ID and a superset structure, generate a prefix for that exercise that can be used in the calendar view.
 * The prefix is a letter (A, B, C, etc.) followed by a number (1, 2, 3, etc.) if the exercise is part of a superset but not a complex.
 * The prefix is used to disambiguate exercises with the same name.
 *
 * @param {string} exerciseId - The ID of the exercise to generate a prefix for
 * @param {array} supersets - The superset structure to generate the prefix from
 * @return {string} The prefix for the exercise
 */
export const calculatePrefixForExercise = (exerciseId, supersets) => {
  if (!supersets) return "";

  const found = supersets.findIndex((group) => group.ids.includes(exerciseId));
  if (found === -1) return "";

  const group = supersets[found];
  const internalIndex = group.ids.indexOf(exerciseId);
  const code = found + 1 + base;

  // Only show subcode (e.g., A1, A2) if it's a true superset, not a complex
  const subcode =
    !group.isComplex && group.ids.length > 1 ? internalIndex + 1 : "";

  return String.fromCharCode(code) + subcode;
};

/**
 * Build an array of supersets from an array of exercises.
 *
 * This function groups exercises by complex or by superset. If an exercise is
 * part of a complex, it is grouped with all other exercises in that complex.
 * If an exercise is not part of a complex, but has a nextSuperset, it is
 * grouped with the previous exercise. If an exercise does not have a
 * nextSuperset, it is grouped alone.
 *
 * The returned array will have the following structure:
 * [
 *   { ids: [exerciseId1, exerciseId2], isComplex: false },
 *   { ids: [exerciseId3, exerciseId4], isComplex: true },
 *   ...
 * ]
 *
 * @param {array} exercises - The array of exercises to group.
 * @return {array} An array of supersets.
 */
/**
 * True when two adjacent exercises belong to the same superset chain or the
 * same complex instance — i.e. a section separator dropped between them would
 * land inside a group.
 */
export const isSameGroup = (a, b) => {
  if (!a || !b) return false;
  if (a.nextSuperset && a.nextSuperset === b._id) return true;
  if (
    a.complex &&
    a.complexInstance &&
    a.complex === b.complex &&
    a.complexInstance === b.complexInstance
  )
    return true;
  return false;
};

export const buildSupersets = (exercises = []) => {
  const supersets = [];
  const usedComplexKeys = new Set(); // Use composite key: complex + instance

  for (let i = 0; i < exercises.length; i++) {
    const exercise = exercises[i];

    // Handle complex grouping
    if (exercise.complex && exercise.complexInstance) {
      const complexKey = `${exercise.complex}_${exercise.complexInstance}`;
      if (!usedComplexKeys.has(complexKey)) {
        const complexExercises = exercises.filter(
          (ex) =>
            ex.complex === exercise.complex &&
            ex.complexInstance === exercise.complexInstance
        );

        usedComplexKeys.add(complexKey);
        supersets.push({
          ids: complexExercises
            .sort((a, b) => a.indexInComplex - b.indexInComplex)
            .map((ex) => ex._id),
          isComplex: true,
        });
      }
      continue;
    }

    const previous = i > 0 ? exercises[i - 1] : null;

    if (
      !previous ||
      !previous.nextSuperset ||
      previous.nextSuperset !== exercise._id
    ) {
      supersets.push({ ids: [exercise._id], isComplex: false });
    } else {
      supersets[supersets.length - 1].ids.push(exercise._id);
    }
  }

  return supersets;
};

/**
 * Where a dragged exercise's whole group (superset or complex, or just the
 * row) ends up, so the page can show it there before the save lands. The
 * server moves the group, and never splits another one, so the pointer slot
 * is not always where it lands: read it from the final order instead.
 *
 * @param {array} originalExercises - The day/session the row was dragged from.
 * @param {string} draggedId - The id of the row that was dragged.
 * @param {string[]} finalIds - The destination's exercise ids after the move.
 * @return {{ ids: string[], following: string[] }} The group in its final
 *   order, and every id after it (the first one with a row to sit before).
 */
export const groupOptimisticMove = (
  originalExercises = [],
  draggedId,
  finalIds = []
) => {
  const order = finalIds.map((id) => String(id));
  const group = buildSupersets(originalExercises).find((entry) =>
    entry.ids.some((id) => String(id) === String(draggedId))
  );
  const ids = (group ? group.ids : [draggedId])
    .map((id) => String(id))
    .sort((a, b) => order.indexOf(a) - order.indexOf(b));
  const last = Math.max(...ids.map((id) => order.indexOf(id)));
  return {
    ids,
    following: order.slice(last + 1).filter((id) => !ids.includes(id)),
  };
};

/**
 * Highest `order` occupied by any exercise OR section in a day/session.
 * Sections share the exercise order space, so "what comes next" must consider
 * both. Returns -1 when empty, so a new exercise (`+ 1`) lands at 0 and a new
 * section (`+ 0.5`) sits just above it.
 *
 * @param {array} exercises
 * @param {array} sections
 * @return {number}
 */
export const getHighestOrder = (exercises = [], sections = []) => {
  // The workout note is never part of the order space (see section-types.js)
  // — excluded here so a caller that forgets to pre-filter can't have it
  // inflate/deflate where the next exercise or separator lands.
  const normalSections = excludeWorkoutNotes(sections);
  const orders = [
    ...exercises.map((e) => e.order ?? 0),
    ...normalSections.map((s) => s.order ?? 0),
  ];
  return orders.length ? Math.max(...orders) : -1;
};

/**
 * Group a day's exercises by section (text separators). Exercises and sections
 * share an `order` space; a section owns every exercise positioned after it
 * until the next section. Exercises before the first section form an implicit
 * default group (`section: null`).
 *
 * @param {array} exercises - the day's exercises (any order; sorted internally)
 * @param {array} sections - the day's sections (any order; sorted internally)
 * @return {array} [{ section: object|null, exercises: array }]
 */
export const groupExercisesBySection = (exercises = [], sections = []) => {
  // The workout note must never open a group — it has no `name`, so an
  // unfiltered caller would render a blank separator above every exercise
  // (the note sorts first by virtue of its WORKOUT_NOTE_ORDER sentinel).
  const normalSections = excludeWorkoutNotes(sections);

  if (!normalSections || normalSections.length === 0) {
    return [{ section: null, exercises: [...exercises] }];
  }

  const items = [
    ...exercises.map((ex) => ({ type: "exercise", order: ex.order ?? 0, ex })),
    ...normalSections.map((s) => ({
      type: "section",
      order: s.order ?? 0,
      section: s,
    })),
  ].sort((a, b) => {
    if (a.order !== b.order) return a.order - b.order;
    if (a.type === b.type) return 0;
    // on ties a section opens before the exercise so the exercise joins it
    return a.type === "section" ? -1 : 1;
  });

  const groups = [{ section: null, exercises: [] }];
  for (const item of items) {
    if (item.type === "section") {
      groups.push({ section: item.section, exercises: [] });
    } else {
      groups[groups.length - 1].exercises.push(item.ex);
    }
  }

  // drop the leading default group if nothing is above the first section
  if (groups[0].section === null && groups[0].exercises.length === 0) {
    groups.shift();
  }

  return groups;
};

/**
 * Given the DOM elements of a session/day list in their new visual order
 * (exercise rows interleaved with `.section-separator` nodes), compute the
 * fractional `order` each section should take so it stays positioned just
 * before the exercise that follows it. Exercises will be renumbered to integer
 * orders 0..N by the backend, so a separator before exercise index `k` sits at
 * `k - 0.5` (a separator at the very top → -0.5). Returns
 * [{ _id, order }] for every separator that carries a `data-section-id`.
 *
 * @param {Iterable<Element>} draggableElements - ordered list of `li`/exercise
 *   rows and `.section-separator` nodes (a static NodeList is fine)
 * @return {array} [{ _id: string, order: number }]
 */
export const computeSectionOrders = (draggableElements = []) =>
  sectionOrdersFromRows(
    Array.from(draggableElements)
      // Defensive: the workout note is never meant to be inside the drag
      // container (see WorkoutNoteBlock.js); if a refactor moved it in, it
      // must not count as an exercise row and shift every section's order.
      .filter((el) => !el.classList?.contains("workout-note"))
      .map((el) => ({
        section: !!el.classList?.contains("section-separator"),
        id: el.getAttribute?.("data-section-id"),
      }))
  );

/**
 * `{ section, id }` rows for a day/session list's draggable elements (exercise
 * rows and `.section-separator` nodes), skipping the workout note.
 */
export const rowsFromDraggables = (elements = []) =>
  Array.from(elements)
    .filter((el) => !el.classList?.contains("workout-note"))
    .map((el) => ({
      section: !!el.classList?.contains("section-separator"),
      id: el.getAttribute?.("data-section-id") || el.id,
    }));

/**
 * Section orders for a day whose exercises are `ids`, when the DOM rows only
 * partly show them. A complex is one row for several ids, and a cross-day
 * drop moves just the grabbed row while the whole superset/complex travels
 * (and leaves its old day). Counting rows would put every section too early
 * on the target and too late on the source.
 *
 * Rows for exercises not in `ids` are dropped; ids that have no row of their
 * own (the rest of a complex or of the dragged group) follow the row before
 * them in `ids`.
 *
 * @param {array} rows - `{ section, id }` in DOM order
 * @param {string[]} ids - the exercise ids the day will hold, in order
 * @param {string[]} [groupIds] - the dragged superset/complex, when it travels
 *   with a single grabbed row
 * @param {string} [grabbedId] - the row that was dragged, for a same-day drop
 *   where the DOM still shows every member of `groupIds`: the other members'
 *   rows are ignored and follow the grabbed one
 * @return {array} [{ _id: string, order: number }]
 */
export const sectionOrdersForIds = (
  rows = [],
  ids = [],
  groupIds = [],
  grabbedId = null
) => {
  const order = ids.map(String);
  const wanted = new Set(order);
  const followers = new Set(
    grabbedId == null
      ? []
      : groupIds.map(String).filter((id) => id !== String(grabbedId))
  );
  const shown = new Set(
    rows
      .filter((row) => !row.section && !followers.has(row.id))
      .map((row) => row.id)
  );
  // Members of the dragged group that sit before the grabbed row lead it.
  // Left to the trailing rule they would follow the row above a section name,
  // putting that name between the members.
  const group = new Set(groupIds.map(String));
  const grabbed = order.find((id) => shown.has(id) && group.has(id));
  const leading = [];
  for (
    let before = order.indexOf(grabbed) - 1;
    grabbed &&
    before >= 0 &&
    !shown.has(order[before]) &&
    group.has(order[before]);
    before--
  )
    leading.unshift(order[before]);
  const layout = [];
  for (const row of rows) {
    if (row.section) {
      layout.push(row);
      continue;
    }
    if (!wanted.has(row.id) || followers.has(row.id)) continue;
    if (row.id === grabbed)
      for (const id of leading) layout.push({ section: false, id });
    layout.push(row);
    for (
      let next = order.indexOf(row.id) + 1;
      next < order.length &&
      !shown.has(order[next]) &&
      !leading.includes(order[next]);
      next++
    )
      layout.push({ section: false, id: order[next] });
  }
  return sectionOrdersFromRows(layout);
};

/**
 * Section orders for a day laid out as rows `{ section, id }` in order.
 * Separators sharing a gap (no exercise between them) must get strictly
 * increasing, distinct orders within (exerciseCount - 1, exerciseCount).
 * A lone separator stays at `exerciseCount - 0.5`; several are spread evenly
 * so they never tie — ties break section reordering/swapping.
 */
export const sectionOrdersFromRows = (rows = []) => {
  const out = [];
  let exerciseCount = 0;
  let gap = [];
  const flushGap = () => {
    const n = gap.length;
    gap.forEach((id, i) => {
      out.push({ _id: id, order: exerciseCount - 1 + (i + 1) / (n + 1) });
    });
    gap = [];
  };
  for (const row of rows) {
    if (row.section) {
      if (row.id) gap.push(row.id);
    } else {
      flushGap();
      // A complex is one row but several exercises, each with its own order.
      exerciseCount += row.ids?.length || 1;
    }
  }
  flushGap();
  return out;
};

/**
 * Rows for a list's draggable elements (exercise rows and section separators,
 * in DOM order). An exercise row stands for its whole complex when it has one.
 * `exercises` is wherever those rows' exercise docs live (the day, or the
 * whole cycle in the program builder).
 */
export const groupRowsFromElements = (elements, exercises = []) =>
  Array.from(elements).map((el) => {
    if (el.classList.contains("section-separator"))
      return {
        section: true,
        id: el.getAttribute("data-section-id"),
        ids: [],
      };
    const exercise = exercises.find((entry) => entry._id === el.id);
    const ids =
      exercise?.complex && exercise?.complexInstance
        ? exercises
            .filter(
              (entry) =>
                entry.complex === exercise.complex &&
                entry.complexInstance === exercise.complexInstance
            )
            .sort((a, b) => a.indexInComplex - b.indexInComplex)
            .map((entry) => entry._id)
        : [el.id];
    return { section: false, id: el.id, ids };
  });

/** Exercise ids of `rows` in order, each once. */
export const exerciseIdsOfRows = (rows) => [
  ...new Set(rows.flatMap((row) => row.ids)),
];

/** Key the drag controller uses to find a row: exercise id, or `section:<id>`. */
export const groupRowKey = (row) =>
  row.section ? `section:${row.id}` : row.id;

/**
 * Where a dragged section group goes and what to tell the controller so the
 * move shows before the save lands. `item` is the section header element,
 * `slot` the drop index in the target's rows, `toRows` null for the same list.
 */
export const planSectionGroupDrop = (item, rows, slot, toRows = null) => {
  const start = rows.findIndex((row) => row.id === item.dataset.sectionId);
  let end = start + 1;
  while (end < rows.length && !rows[end].section) end++;
  const {
    fromRows,
    toRows: newToRows,
    group,
  } = moveSectionGroup(rows, { start, end }, slot, toRows);
  const landed = newToRows || fromRows;
  const landedAt = landed.indexOf(group[0]);
  return {
    fromRows,
    toRows: newToRows,
    optimistic: {
      ids: group.map(groupRowKey),
      following: landed.slice(landedAt + group.length).map(groupRowKey),
    },
  };
};

/**
 * Move a section group (header + its rows, `range` in `rows`) to `slot`, an
 * index into the target's rows as they are now. Rows are `{ section, id,
 * ids }`. Same list when `toRows` is null.
 */
export const moveSectionGroup = (rows, range, slot, toRows = null) => {
  const group = rows.slice(range.start, range.end);
  const rest = [...rows.slice(0, range.start), ...rows.slice(range.end)];
  if (toRows) {
    const target = [...toRows];
    target.splice(slot, 0, ...group);
    return { fromRows: rest, toRows: target, group };
  }
  const at = slot >= range.end ? slot - group.length : slot;
  rest.splice(at, 0, ...group);
  return { fromRows: rest, toRows: null, group };
};

export const orderAmongSections = (item) => {
  const orderOf = (el) => {
    const raw = el?.classList?.contains("section-separator")
      ? el.getAttribute("data-order")
      : null;
    return raw == null || raw === "" ? null : Number(raw);
  };
  const prev = orderOf(item.previousElementSibling);
  const next = orderOf(item.nextElementSibling);
  if (prev != null && next != null) return (prev + next) / 2;
  if (prev != null) return prev + 0.5;
  if (next != null) return next - 0.5;
  return 0;
};

/**
 * Compute the prefix (A, B1, B2, …) for every exercise in a day, restarting at
 * "A" inside each section. Returns a map keyed by exercise id. With no sections
 * this reproduces the single-group behaviour exactly.
 *
 * @param {array} exercises
 * @param {array} sections
 * @return {object} { [exerciseId]: prefix }
 */
export const computeSectionPrefixes = (exercises = [], sections = []) => {
  const groups = groupExercisesBySection(exercises, sections);
  const prefixes = {};

  groups.forEach((group) => {
    const supersets = buildSupersets(group.exercises);
    group.exercises.forEach((ex) => {
      prefixes[ex._id] = calculatePrefixForExercise(ex._id, supersets);
    });
  });

  return prefixes;
};

/**
 * Determine whether applying a new exercise order would split a genuine
 * superset or complex group.
 *
 * Genuine groups are derived from the exercises' *current* order via
 * buildSupersets, which only forms a multi-id group when a head's nextSuperset
 * matches the exercise immediately following it. A "ghost" superset (a stale
 * nextSuperset pointing at an exercise that is not its immediate successor) is
 * therefore treated as a singleton and never blocks reordering.
 *
 * A move is blocked only when it would break the contiguity/order of a real
 * group. Moving a whole group together keeps its members contiguous and is
 * allowed.
 *
 * @param {array} originalExercises - The exercises in their current order.
 * @param {array} newOrderedIds - The exercise ids in the proposed new order.
 * @return {boolean} True if the reorder would split a real group.
 */
export const reorderBreaksSuperset = (
  originalExercises = [],
  newOrderedIds = []
) => {
  const order = newOrderedIds.map((id) => String(id));
  const groups = buildSupersets(originalExercises).filter(
    (group) => group.ids.length > 1
  );

  return groups.some((group) => {
    const ids = group.ids.map((id) => String(id));
    const start = order.indexOf(ids[0]);
    // Head moved out of this list entirely; handled by the other list's check.
    if (start === -1) return false;
    // Members must remain contiguous and in the same order.
    return ids.some((id, k) => order[start + k] !== id);
  });
};

/**
 * Reorder a list while keeping superset/complex groups atomic.
 *
 * The drag UI moves one row at a time, so a naive new order can split a group.
 * This treats each genuine group (from buildSupersets on the *original* order)
 * as a single block and re-emits whole blocks, ordered by an anchor position in
 * the proposed order:
 *  - the block containing the dragged row anchors at the dragged row's
 *    position, so the whole group follows the drop (drag head or tail, the
 *    group lands where you dropped it);
 *  - a superset row dropped inside its own superset keeps the new order of
 *    the members (reorder within a superset); complexes keep theirs;
 *  - every other block anchors at its first-seen member, so it stays put;
 *  - dropping an outside exercise "into" a group lands it adjacent to the
 *    block (the group is never split);
 *  - ghost pointers form singletons in buildSupersets, so they move freely.
 *
 * When draggedId is omitted, blocks anchor at their first-seen member. Ids not
 * present in the original list (e.g. an item dragged in from another list) are
 * emitted as their own single-item block.
 *
 * @param {array} originalExercises - The exercises in their current order.
 * @param {array} newOrderedIds - The proposed new order of exercise ids.
 * @param {string} [draggedId] - The id of the row that was dragged.
 * @return {string[]} The corrected order with groups kept contiguous.
 */
export const reorderPreservingSupersets = (
  originalExercises = [],
  newOrderedIds = [],
  draggedId = null
) => {
  const groups = buildSupersets(originalExercises);
  const blocks = groups.map((group) => group.ids.map((id) => String(id)));
  const blockByMember = new Map();
  blocks.forEach((members, index) => {
    for (const member of members) blockByMember.set(member, index);
  });

  const dragged = draggedId == null ? null : String(draggedId);
  const draggedBlock =
    dragged !== null && blockByMember.has(dragged)
      ? blockByMember.get(dragged)
      : null;

  // Assign each block (and each non-grouped id) an anchor position in the new
  // order. The dragged block anchors on the dragged row; everything else on its
  // first-seen member.
  const order = newOrderedIds.map((id) => String(id));
  const anchors = new Map();
  order.forEach((id, position) => {
    const key = blockByMember.has(id) ? blockByMember.get(id) : `solo:${id}`;
    if (key === draggedBlock) {
      if (id === dragged) anchors.set(key, position);
    } else if (!anchors.has(key)) {
      anchors.set(key, position);
    }
  });

  // Dropped inside its own superset (members still contiguous): keep the
  // members in their dropped order instead of snapping back.
  if (draggedBlock !== null && !groups[draggedBlock].isComplex) {
    const members = blocks[draggedBlock];
    const positions = members.map((member) => order.indexOf(member));
    const first = Math.min(...positions);
    if (
      !positions.includes(-1) &&
      Math.max(...positions) - first === members.length - 1
    ) {
      blocks[draggedBlock] = order.slice(first, first + members.length);
    }
  }

  const result = [];
  const orderedKeys = [...anchors.keys()].sort(
    (a, b) => anchors.get(a) - anchors.get(b)
  );
  for (const key of orderedKeys) {
    if (typeof key === "number") {
      for (const member of blocks[key]) result.push(member);
    } else {
      result.push(key.slice("solo:".length));
    }
  }
  return result;
};

/**
 * Move a dragged row (and, if it belongs to one, its whole superset/complex
 * group) from one list to another, keeping every group atomic.
 *
 * The drag UI only relocates the single dragged <li>, so a group dragged across
 * sessions would otherwise be split. This pulls the dragged row's entire group
 * out of the source list and inserts it as one contiguous block at the drop
 * position in the destination, while keeping the destination's own groups
 * intact (an incoming block is never dropped inside another group).
 *
 * @param {array} sourceOriginal - Source list exercises in their current order
 *   (must include the dragged row so its group can be detected).
 * @param {array} destOriginal - Destination list exercises in their current
 *   order (without the dragged row).
 * @param {string} draggedId - The id of the row that was dragged.
 * @param {array} newFromIds - Proposed source-list ids after the drag.
 * @param {array} newToIds - Proposed destination-list ids after the drag.
 * @return {{fromIds: string[], toIds: string[], groupIds: string[]}}
 */
export const moveSupersetAcrossLists = (
  sourceOriginal = [],
  destOriginal = [],
  draggedId,
  newFromIds = [],
  newToIds = []
) => {
  const dragged = String(draggedId);
  const sourceBlocks = buildSupersets(sourceOriginal).map((group) =>
    group.ids.map((id) => String(id))
  );
  const group = sourceBlocks.find((block) => block.includes(dragged)) || [
    dragged,
  ];
  const groupSet = new Set(group);

  // Source loses the whole group.
  const fromIds = newFromIds
    .map((id) => String(id))
    .filter((id) => !groupSet.has(id));

  // Destination: keep its own groups atomic and insert the imported group as a
  // contiguous block at the dragged row's drop position.
  const destBlocks = buildSupersets(destOriginal).map((b) =>
    b.ids.map((id) => String(id))
  );
  const destBlockByMember = new Map();
  destBlocks.forEach((members, index) => {
    for (const member of members) destBlockByMember.set(member, index);
  });

  const emittedDest = new Set();
  const toIds = [];
  let groupInserted = false;
  for (const rawId of newToIds) {
    const id = String(rawId);
    if (groupSet.has(id)) {
      if (id === dragged && !groupInserted) {
        toIds.push(...group);
        groupInserted = true;
      }
      continue;
    }
    const blockIndex = destBlockByMember.has(id)
      ? destBlockByMember.get(id)
      : null;
    if (blockIndex === null) {
      toIds.push(id);
    } else if (!emittedDest.has(blockIndex)) {
      emittedDest.add(blockIndex);
      toIds.push(...destBlocks[blockIndex]);
    }
  }
  if (!groupInserted) toIds.push(...group);

  return { fromIds, toIds, groupIds: group };
};

/**
 * The superset chains (member ids, in order) after a reorder, for the
 * reorder endpoints' `supersets` field. The API relinks `nextSuperset` from
 * them; without it, a superset reordered internally looks like a ghost
 * pointer and is dissolved. Complexes are left out (not pointer-linked).
 *
 * @param {array} originalExercises - The exercises in their current order.
 * @param {array} orderedIds - The exercise ids in their new order.
 * @return {string[][]} One array of ids per superset of 2+ exercises.
 */
export const supersetChains = (originalExercises = [], orderedIds = []) => {
  const order = orderedIds.map((id) => String(id));
  return buildSupersets(originalExercises)
    .filter((group) => !group.isComplex && group.ids.length > 1)
    .map((group) => {
      const ids = group.ids.map((id) => String(id));
      return order.filter((id) => ids.includes(id));
    });
};
